import React from 'react';
import { Drawer, List, ListItem, ListItemText, Toolbar, AppBar, Typography, Box, CssBaseline, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BarChartIcon from '@mui/icons-material/BarChart';
import CommentIcon from '@mui/icons-material/Comment';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import TuneIcon from '@mui/icons-material/Tune';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CampaignIcon from '@mui/icons-material/Campaign';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const drawerWidth = 210;

const SidebarLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isAdmin = user?.role === 'admin';
  const perms = user?.permissions || ['bili_barrage', 'bili_comment'];
  const hasPerm = (p: string) => isAdmin || perms.includes(p);

  const menuItems = [
    { text: 'Bili 弹幕统计', path: '/dashboard', icon: <BarChartIcon />, permission: 'bili_barrage' },
    { text: 'Bili 评论统计', path: '/commentDashboard', icon: <CommentIcon />, permission: 'bili_comment' },
    { text: '视频播放统计', path: '/playDashboard', icon: <PlayCircleIcon />, permission: 'bili_play' },
    { text: '小视频统计', path: '/statVideoDashboard', icon: <MovieFilterIcon />, permission: 'bili_play' },
    { text: '操作记录', path: '/use-records', icon: <HistoryIcon /> },
    { text: '规则配置', path: '/bili-config', icon: <TuneIcon />, adminOnly: true },
    { text: '文案管理', path: '/copy-texts', icon: <TextFieldsIcon />, adminOnly: true },
    { text: '视频列表', path: '/videos', icon: <VideoLibraryIcon />, adminOnly: true },
    { text: '公告管理', path: '/announcements', icon: <CampaignIcon />, adminOnly: true },
    { text: '用户管理', path: '/users', icon: <PeopleIcon />, adminOnly: true },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.adminOnly) return isAdmin;
    if (item.permission) return hasPerm(item.permission);
    return true;
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: 1201 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap component="div">
            B站后台管理系统
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user && (
              <Typography variant="body1" sx={{ mr: 2 }}>
                {user.username}
              </Typography>
            )}
            <Button color="inherit" onClick={logout} size="small" variant="outlined">
              退出
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {filteredMenuItems.map((item) => {
              const selected = location.pathname === item.path;
              return (
                <ListItem
                  key={item.path}
                  button
                  selected={selected}
                  onClick={() => navigate(item.path)}
                  sx={{
                    my: 0.5,
                    px: 2,
                    minHeight: 44,
                    ...(selected
                      ? {
                          bgcolor: 'action.selected',
                          color: 'primary.main',
                          borderRight: '4px solid',
                          borderRightColor: 'primary.main',
                          fontWeight: 'bold',
                          borderRadius: 0,
                        }
                      : {
                          color: 'text.primary',
                          borderRight: '4px solid transparent',
                          '&:hover': { bgcolor: 'grey.100' },
                        }),
                  }}
                >
                  {item.icon}
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      color: selected ? 'primary.main' : 'text.primary',
                      fontWeight: selected ? 'bold' : 'normal',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, ml: `${drawerWidth}px`, minWidth: 0 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
